import { supabase } from '@/lib/supabase';
import { kioskSuspendedFor } from '@/lib/stationStatus';
import { isAdmin } from '@/lib/roles';
import type {
  DemoLocation,
  DemoStation,
  DemoStationCategory,
  DemoStationWithDetails,
  DemoCommand,
  DemoCommandType,
  DemoStationLog,
  AgentRelease,
} from '@/types/demo';

// ── Locations ────────────────────────────────────────────────

export async function getDemoLocations(): Promise<DemoLocation[]> {
  const { data, error } = await supabase
    .from('demo_locations')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');
  if (error) throw error;
  return data ?? [];
}

/** Includes deactivated shops — the manage-shops dialog needs them to offer a restore. */
export async function getAllDemoLocations(): Promise<DemoLocation[]> {
  const { data, error } = await supabase
    .from('demo_locations')
    .select('*')
    .order('sort_order');
  if (error) throw error;
  return data ?? [];
}

/** Station count per location id, so a shop that still has PCs can't be removed. */
export async function getStationCountsByLocation(): Promise<Record<string, number>> {
  const { data, error } = await supabase.from('demo_stations').select('location_id');
  if (error) throw error;
  const counts: Record<string, number> = {};
  (data ?? []).forEach((row: { location_id: string | null }) => {
    if (row.location_id) counts[row.location_id] = (counts[row.location_id] ?? 0) + 1;
  });
  return counts;
}

export async function createDemoLocation(payload: {
  name: string;
  shop_code: string;
  address?: string;
  sort_order?: number;
}): Promise<DemoLocation> {
  const { data, error } = await supabase
    .from('demo_locations')
    .insert(payload)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateDemoLocation(id: string, payload: Partial<DemoLocation>): Promise<void> {
  const { error } = await supabase.from('demo_locations').update(payload).eq('id', id);
  if (error) throw error;
}

// ── Stations ─────────────────────────────────────────────────

/**
 * The columns the stations grid actually renders.
 *
 * This was `*`, which is 50 columns - every telemetry reading, every timestamp,
 * the whole HWiNFO surface - to draw a tile showing a name, a status dot and a
 * screenshot. The grid reads 23 of them.
 *
 * That mattered out of proportion to its size because of how often it runs.
 * Each station UPDATEs demo_stations on its 30s heartbeat and again on its 60s
 * screenshot, every one of those fires the page's Realtime subscription, and
 * every one of those refetched the entire list. One station's heartbeat pulled
 * all 50 columns of every other station, so the cost grew with the square of
 * the fleet. On 2026-09-23 PostgREST egress was 450MB in a day, 99.2% of the
 * project total, against a 5GB monthly allowance.
 *
 * Kept as an explicit list rather than a Pick<> of the row type so that adding
 * a column to the table does not silently start shipping it to every tile.
 */
const STATION_LIST_COLUMNS = [
  'id', 'name', 'machine_id', 'serial_number', 'status', 'category',
  'location_id', 'build_id', 'agent_version', 'notes',
  'last_seen', 'screenshot_url', 'screenshot_at',
  'hud_enabled', 'ad_enabled', 'video_enabled', 'demo_mode',
  'admin_mode', 'admin_mode_persistent', 'admin_mode_since',
  'usb_unlocked_until', 'pending_updates', 'retire_phase',
].join(', ');

export async function getDemoStations(
  locationId?: string,
  category?: DemoStationCategory,
): Promise<DemoStationWithDetails[]> {
  let query = supabase
    .from('demo_stations')
    .select(`
      ${STATION_LIST_COLUMNS},
      demo_locations!location_id ( id, name, shop_code, sort_order ),
      builds!build_id ( sku, description )
    `)
    .order('name');

  if (locationId) query = query.eq('location_id', locationId);
  if (category)   query = query.eq('category', category);

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map((s: any) => ({
    ...s,
    location: s.demo_locations ?? null,
    build_sku: s.builds?.sku ?? null,
    build_description: s.builds?.description ?? null,
    demo_locations: undefined,
    builds: undefined,
  }));
}

export async function getDemoStation(id: string): Promise<DemoStationWithDetails> {
  const { data, error } = await supabase
    .from('demo_stations')
    .select(`
      *,
      demo_locations!location_id ( id, name, shop_code, sort_order ),
      builds!build_id ( sku, description )
    `)
    .eq('id', id)
    .single();
  if (error) throw error;
  return {
    ...data,
    location: (data as any).demo_locations ?? null,
    build_sku: (data as any).builds?.sku ?? null,
    build_description: (data as any).builds?.description ?? null,
  };
}

export async function updateDemoStation(id: string, updates: Partial<DemoStation>): Promise<void> {
  const { error } = await supabase.from('demo_stations').update(updates).eq('id', id);
  if (error) throw error;
}

export async function deleteDemoStation(id: string): Promise<void> {
  const { error } = await supabase.from('demo_stations').delete().eq('id', id);
  if (error) throw error;
}

// ── Commands ─────────────────────────────────────────────────

/**
 * Commands the database refuses unless the caller is admin or super_admin:
 * the mirror of public.is_privileged_demo_command() in
 * 20261007200000_lock_down_demo_commands.sql. Keep the two in step. This list
 * only decides which buttons show; the database decides what runs.
 *
 * Demo Mode (unlock_kiosk), Enable USB, reboot and shutdown are deliberately
 * absent - shop staff use them, and none of them hands over the machine.
 */
export const ADMIN_ONLY_COMMANDS: readonly DemoCommandType[] = [
  'start_shell', 'start_remote', 'update_agent',
  'admin_mode', 'unlock_task_manager', 'update_windows', 'retire',
];

export function canSendCommand(role: string | null | undefined, cmd: DemoCommandType): boolean {
  return !ADMIN_ONLY_COMMANDS.includes(cmd) || isAdmin(role);
}

export async function sendCommand(
  stationId: string,
  commandType: DemoCommandType,
  payload?: Record<string, unknown>,
  createdBy?: string,
): Promise<DemoCommand> {
  const { data, error } = await supabase
    .from('demo_commands')
    .insert({
      station_id: stationId,
      command_type: commandType,
      payload: payload ?? {},
      created_by: createdBy ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Send a ping command and wait for the agent's pong reply.
 * Polls the command row every second until status flips to completed/failed
 * or the timeout elapses. Throws on failure / timeout / DB error.
 */
export async function pingStation(
  stationId: string,
  createdBy?: string,
  timeoutMs = 8_000,
): Promise<{ hostname: string; uptime: number }> {
  const cmd = await sendCommand(stationId, 'ping', {}, createdBy);
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 1_000));
    const { data, error } = await supabase
      .from('demo_commands')
      .select('status, result')
      .eq('id', cmd.id)
      .single();
    if (error) throw error;
    if (data.status === 'completed') {
      const r = (data.result ?? {}) as { hostname?: string; uptime?: number };
      return { hostname: r.hostname ?? 'unknown', uptime: r.uptime ?? 0 };
    }
    if (data.status === 'failed') {
      const r = (data.result ?? {}) as { error?: string };
      throw new Error(r.error ?? 'Agent reported failure');
    }
  }
  throw new Error(`No response after ${Math.round(timeoutMs / 1000)}s`);
}

export async function sendBulkCommand(
  stationIds: string[],
  commandType: DemoCommandType,
  payload?: Record<string, unknown>,
  createdBy?: string,
): Promise<void> {
  const rows = stationIds.map((station_id) => ({
    station_id,
    command_type: commandType,
    payload: payload ?? {},
    created_by: createdBy ?? null,
  }));
  const { error } = await supabase.from('demo_commands').insert(rows);
  if (error) throw error;
}

export async function getStationCommands(stationId: string, limit = 30): Promise<DemoCommand[]> {
  const { data, error } = await supabase
    .from('demo_commands')
    .select('*')
    .eq('station_id', stationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

// ── HUD ──────────────────────────────────────────────────────

const HUD_TYPES = ['CPU', 'GPU', 'RAM', 'ROM', 'OS'] as const;
const HUD_TYPE_LABELS: Record<string, string> = {
  CPU: 'Processor', GPU: 'Graphics', RAM: 'Memory', ROM: 'Storage', OS: 'OS',
};

/**
 * Fetch build specs and push an update_hud command to a station.
 * The agent renders the spec overlay from the command payload.
 */
export async function sendHudUpdate(
  stationId: string,
  buildId: string,
  userName?: string,
): Promise<void> {
  // Derive product family from SKU (format: aoi-{family_letter}{tier}-...)
  function getFamilyFromSku(sku: string): string | null {
    const seg = sku.toLowerCase().split('-')[1];
    if (!seg) return null;
    const map: Record<string, string> = { l:'legend', z:'zero', p:'pro', m:'master', e:'elite', '1':'one' };
    return map[seg[0]] ?? null;
  }

  // Build header (SKU, description, RRP) + active_config_skus (ticked components)
  const { data: build, error: buildErr } = await supabase
    .from('builds')
    .select('sku, description, rrp, active_config_skus, web_url')
    .eq('id', buildId)
    .single();
  if (buildErr) throw buildErr;

  // Parse the ticked SKUs from build detail page
  const activeSkuSet = new Set<string>(
    ((build as any).active_config_skus ?? '')
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean),
  );

  // Key components for this build
  let { data: comps, error: compErr } = await supabase
    .from('build_components')
    .select('component_type, component_sku')
    .eq('build_id', buildId)
    .in('component_type', HUD_TYPES);
  if (compErr) throw compErr;

  // If build_components is empty, fall back to the legacy components.build_sku
  // field. Read-only: this used to save the result as the build's BOM, but only
  // managers and above may write build_components, and the stations page is
  // used by retail, marketing and technicians - for them the insert failed and
  // the HUD went out with no specs.
  if (!comps || comps.length === 0) {
    const { data: legacyComps } = await supabase
      .from('components')
      .select('part_number, category, build_sku')
      .ilike('build_sku', `%${build.sku}%`);

    comps = (legacyComps ?? [])
      .filter((c: any) => {
        const entries = (c.build_sku ?? '').split(',').map((s: string) => s.trim().replace(/-EOL$/i, ''));
        return entries.includes(build.sku) && (HUD_TYPES as readonly string[]).includes(c.category);
      })
      .map((c: any) => ({ component_type: c.category, component_sku: c.part_number }));
  }

  // Resolve display names — prefer b2c name, fall back to component description
  const skus = (comps ?? []).map((c: any) => c.component_sku).filter(Boolean);
  const nameMap: Record<string, string> = {};
  if (skus.length > 0) {
    const [dnRes, descRes] = await Promise.all([
      supabase.from('component_display_names').select('part_number, display_name_b2c').in('part_number', skus),
      supabase.from('components').select('part_number, description').in('part_number', skus),
    ]);
    (descRes.data ?? []).forEach((r: any) => { if (r.description)      nameMap[r.part_number] = r.description; });
    (dnRes.data  ?? []).forEach((r: any) => { if (r.display_name_b2c) nameMap[r.part_number] = r.display_name_b2c; });
  }

  // Assemble specs in display order.
  // ROM: show ALL drives joined with " + " (e.g. "1TB SSD + 2TB HDD").
  // Other types: prefer the ticked (active) component, fall back to first.
  const specs = (HUD_TYPES as readonly string[])
    .map((type) => {
      const typeComps = (comps ?? []).filter((c: any) => c.component_type === type);
      if (!typeComps.length) return null;

      if (type === 'ROM') {
        const drives = (activeSkuSet.size > 0 ? typeComps.filter((c: any) => activeSkuSet.has(c.component_sku)) : typeComps)
          .map((c: any) => nameMap[c.component_sku] ?? c.component_sku);
        const value = drives.length > 0 ? drives.join(' + ') : (nameMap[typeComps[0].component_sku] ?? typeComps[0].component_sku);
        return { label: HUD_TYPE_LABELS[type] ?? type, value };
      }

      const comp = activeSkuSet.size > 0
        ? (typeComps.find((c: any) => activeSkuSet.has(c.component_sku)) ?? typeComps[0])
        : typeComps[0];
      return {
        label: HUD_TYPE_LABELS[type] ?? type,
        value: nameMap[comp.component_sku] ?? comp.component_sku,
      };
    })
    .filter(Boolean);

  // APM score
  const { data: benchmark } = await supabase
    .from('build_benchmarks')
    .select('passmark_rating')
    .eq('build_id', buildId)
    .maybeSingle();

  await sendCommand(stationId, 'update_hud', {
    build_id:    buildId,
    sku:         build.sku,
    description: build.description,
    rrp:         build.rrp,
    web_url:     (build as any).web_url ?? null,
    apm_score:   benchmark?.passmark_rating ?? null,
    family:      getFamilyFromSku(build.sku ?? ''),
    specs,
    visible: true,
  }, userName);
}

// ── Demo mode ─────────────────────────────────────────────────

/**
 * Enter or leave demo mode on one station.
 *
 * Demo mode hides every overlay and unlocks the kiosk so a customer can use
 * the machine. Leaving it has to put back what was there *before* — which
 * means capturing all three overlay states on the way in, not just the HUD.
 * Restoring `video_enabled: true, ad_enabled: true` unconditionally is what
 * used to resurrect an ad an operator had deliberately switched off.
 *
 * `?? true` on the way out covers a station that entered demo mode before
 * those columns existed: it restores to enabled, which is what it would have
 * done anyway.
 *
 * Lives here rather than in the pages because both the stations list and the
 * station detail page drive it, and when it was duplicated the two copies
 * carried the same bug.
 */
export type DemoOverlayState = { hud: boolean; video: boolean; ad: boolean };

export type DemoModeStation = Pick<DemoStation,
  'id' | 'hud_enabled' | 'video_enabled' | 'ad_enabled'
  | 'pre_demo_hud_enabled' | 'pre_demo_video_enabled' | 'pre_demo_ad_enabled'>;

/**
 * What the three overlays should be once the toggle lands. The pure core of
 * setDemoMode, and the part where the bug was: entering captures all three,
 * leaving restores all three. Returning one object also stops the database
 * update and the agent commands drifting apart, since both read from it.
 */
export function demoModeOverlays(station: DemoModeStation, enter: boolean): DemoOverlayState {
  if (enter) return { hud: false, video: false, ad: false };
  return {
    hud:   station.pre_demo_hud_enabled   ?? true,
    video: station.pre_demo_video_enabled ?? true,
    ad:    station.pre_demo_ad_enabled    ?? true,
  };
}

export async function setDemoMode(
  station: DemoModeStation & { admin_mode?: boolean },
  enter: boolean,
  userName?: string,
): Promise<void> {
  // Demo Mode used to close Admin Mode on its way out, because leaving it
  // restored the kiosk and would have done so with the policies and USB still
  // lifted. It no longer restores the kiosk while Admin Mode is on - the agent
  // keeps the shell suspended for the technician - so the two are left alone.
  const next = demoModeOverlays(station, enter);
  const adminOn = station.admin_mode === true;

  if (enter) {
    await supabase.from('demo_stations').update({
      pre_demo_hud_enabled:   station.hud_enabled,
      pre_demo_video_enabled: station.video_enabled,
      pre_demo_ad_enabled:    station.ad_enabled,
      hud_enabled: next.hud, video_enabled: next.video, ad_enabled: next.ad,
      demo_mode: true,
      kiosk_suspended: kioskSuspendedFor(true, adminOn),
    }).eq('id', station.id);

    await Promise.allSettled([
      // The agent reads payload.duration_minutes — sending `minutes` here meant
      // Demo Mode silently re-locked after the 30-minute default, not 8 hours.
      sendCommand(station.id, 'unlock_kiosk', { duration_minutes: 480 }, userName),
      sendCommand(station.id, 'toggle_hud',   { enabled: next.hud }, userName),
      sendCommand(station.id, 'toggle_video', { enabled: next.video }, userName),
      sendCommand(station.id, 'toggle_ad',    { enabled: next.ad }, userName),
    ]);
    return;
  }

  await supabase.from('demo_stations').update({
    hud_enabled: next.hud, video_enabled: next.video, ad_enabled: next.ad,
    pre_demo_hud_enabled: null, pre_demo_video_enabled: null, pre_demo_ad_enabled: null,
    demo_mode: false,
    // Stays suspended when a technician still has Admin Mode open.
    kiosk_suspended: kioskSuspendedFor(false, adminOn),
  }).eq('id', station.id);

  await Promise.allSettled([
    sendCommand(station.id, 'lock_kiosk',   {}, userName),
    sendCommand(station.id, 'toggle_hud',   { enabled: next.hud }, userName),
    sendCommand(station.id, 'toggle_video', { enabled: next.video }, userName),
    sendCommand(station.id, 'toggle_ad',    { enabled: next.ad }, userName),
  ]);
}

// ── Admin Mode / USB overrides ────────────────────────────────
//
// Demo Mode hides the overlays for a customer.  Admin Mode is the technician
// equivalent and goes much further: it lifts the elevated registry policies
// (Run, Control Panel, Task Manager, context menu), unblocks USB storage and
// restores the Explorer shell, which is what motherboard vendor installers need.

/** Minutes Admin Mode stays open before the agent re-locks the station. */
export const ADMIN_MODE_MINUTES = 480;

/** Minutes a USB-only unlock stays open. */
export const USB_UNLOCK_MINUTES = 60;

/** What the agent is told when Admin Mode is switched. */
export type AdminModePayload = {
  enabled: boolean;
  duration_minutes?: number;
  indefinite?: true;
};

/**
 * The pure core of setAdminMode, and the part that decides whether the station
 * ever re-locks. A station flagged admin_mode_persistent gets no timer at all.
 *
 * That is sent as an explicit `indefinite` flag rather than as an absent
 * duration because the two are not the same to the agent: it reads a missing
 * deadline as "written by a build too old to record one" and re-arms a full
 * window at the next boot, which would quietly turn a permanent unlock back
 * into an 8h one on the first reboot.
 */
export function adminModePayload(
  station: { admin_mode_persistent?: boolean },
  enabled: boolean,
): AdminModePayload {
  if (!enabled) return { enabled: false };
  return station.admin_mode_persistent === true
    ? { enabled: true, indefinite: true }
    : { enabled: true, duration_minutes: ADMIN_MODE_MINUTES };
}

export async function setAdminMode(
  station: DemoModeStation & { demo_mode?: boolean; admin_mode_persistent?: boolean },
  enabled: boolean,
  userName?: string,
): Promise<void> {
  // Admin Mode used to enter Demo Mode first, to get the Explorer shell back
  // and the overlays out of the way. It no longer touches either: suspending
  // the shell is its own doing, and the overlays stay exactly as they were -
  // demoted from always-on-top by the agent, so an installer window sits above
  // them, but still showing whatever the station was showing.
  const demoOn = station.demo_mode === true;

  // Written optimistically so the button flips immediately; the agent confirms
  // all three flags via syncKioskState once it has actually applied the change.
  await supabase.from('demo_stations').update({
    admin_mode:      enabled,
    // Only Demo Mode can hold the shell down once Admin Mode lets go.
    kiosk_suspended: kioskSuspendedFor(demoOn, enabled),
    // Start the clock the dashboard's "left open and forgotten" warning reads.
    // The agent stamps this again when it actually applies the change; both are
    // the same transition, seconds apart.
    admin_mode_since: enabled ? new Date().toISOString() : null,
  }).eq('id', station.id);

  await sendCommand(station.id, 'admin_mode', adminModePayload(station, enabled), userName);
}

/**
 * Unblock USB storage without dropping the kiosk shell - for handing someone a
 * USB stick on a station that should otherwise stay locked down. Toggleable:
 * the agent re-blocks on its own timer, or immediately when switched off here.
 */
export async function setUsbUnlocked(
  stationId: string,
  enabled: boolean,
  userName?: string,
  minutes: number = USB_UNLOCK_MINUTES,
): Promise<void> {
  // Optimistic, so the badge and countdown appear on click rather than after the
  // agent's next poll. The agent overwrites this with its real deadline.
  await supabase.from('demo_stations').update({
    usb_unlocked_until: enabled
      ? new Date(Date.now() + minutes * 60_000).toISOString()
      : null,
  }).eq('id', stationId);

  await sendCommand(stationId, 'unlock_usb', { enabled, duration_minutes: minutes }, userName);
}

// ── Agent releases ────────────────────────────────────────────

export async function getLatestRelease(): Promise<AgentRelease | null> {
  const { data, error } = await supabase
    .from('agent_releases')
    .select('*')
    .eq('is_current', true)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function getReleaseHistory(limit = 10): Promise<AgentRelease[]> {
  const { data, error } = await supabase
    .from('agent_releases')
    .select('*')
    .order('published_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export async function publishRelease(payload: {
  version: string;
  download_url: string;
  release_notes?: string;
  published_by: string;
}): Promise<void> {
  // Clear current flag on any existing release
  await supabase
    .from('agent_releases')
    .update({ is_current: false })
    .eq('is_current', true);

  const { error } = await supabase
    .from('agent_releases')
    .insert({ ...payload, is_current: true });
  if (error) throw error;
}

// ── Logs ─────────────────────────────────────────────────────

export async function getStationLogs(stationId: string, limit = 50): Promise<DemoStationLog[]> {
  const { data, error } = await supabase
    .from('demo_station_logs')
    .select('*')
    .eq('station_id', stationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

// ── Helpers ───────────────────────────────────────────────────
//
// Re-exported from @/lib/stationStatus, which holds no Supabase import so the
// helpers can be tested without the client throwing on missing env vars.
export { isStationOnline, formatUptime, formatLastSeen, isUsbUnlocked, formatRemaining,
  isAdminModeStale, formatAdminModeAge, ADMIN_MODE_STALE_DAYS, kioskSuspendedFor } from '@/lib/stationStatus';
